type ModelConfidence = 'exact' | 'likely' | 'approximate';

export type DevicePayload = {
  slug: string;
  phoneModel: string;
  brand: string | null;
  deviceType: string;
  os: string | null;
  osVersion: string | null;
  androidVersion: string | null;
  iosVersion: string | null;
  browser: string | null;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  screenKey: string;
  gpuRenderer: string | null;
  modelSource: 'client-detect';
  modelConfidence: ModelConfidence;
};

function getWebGlRenderer() {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') ||
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
    if (!gl) return null;

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return null;

    return gl.getParameter(
      // `WEBGL_debug_renderer_info` isn't part of the default TS DOM types.
      (debugInfo as unknown as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL
    ) as string;
  } catch {
    return null;
  }
}

function getScreenKey() {
  const width = Math.min(window.screen.width, window.screen.height);
  const height = Math.max(window.screen.width, window.screen.height);
  return {
    width,
    height,
    key: `${width}x${height}`,
  };
}

function parseIosVersion(userAgent: string) {
  const versionTag = userAgent.match(/Version\/(\d+\.\d+(?:\.\d+)?)/);
  if (versionTag && /iPhone|iPad/i.test(userAgent)) {
    return versionTag[1];
  }

  const osTag = userAgent.match(/OS (\d+)[_.](\d+)(?:[_.](\d+))?/);
  if (!osTag) return null;
  return osTag[3] ? `${osTag[1]}.${osTag[2]}.${osTag[3]}` : `${osTag[1]}.${osTag[2]}`;
}

function parseAppleModel(
  userAgent: string,
  screenKey: string,
  gpuRenderer: string | null
): { phoneModel: string; brand: string; confidence: ModelConfidence } {
  const isIpad = /iPad/i.test(userAgent);
  if (isIpad) {
    return {
      phoneModel: `iPad (${screenKey})`,
      brand: 'Apple',
      confidence: 'approximate',
    };
  }

  const exactMap: Record<string, string> = {
    'Apple A19 Pro GPU|440x956': 'iPhone 17 Pro Max',
    'Apple A19 Pro GPU|402x874': 'iPhone 17 Pro',
    'Apple A18 Pro GPU|440x956': 'iPhone 16 Pro Max',
    'Apple A18 Pro GPU|402x874': 'iPhone 16 Pro',
    'Apple A17 Pro GPU|430x932': 'iPhone 15 Pro Max',
    'Apple A17 Pro GPU|393x852': 'iPhone 15 Pro',
    'Apple A16 GPU|430x932': 'iPhone 14 Pro Max',
    'Apple A16 GPU|393x852': 'iPhone 14 Pro',
  };

  if (gpuRenderer) {
    const exact = exactMap[`${gpuRenderer}|${screenKey}`];
    if (exact) {
      return { phoneModel: exact, brand: 'Apple', confidence: 'exact' };
    }
  }

  const fallbackMap: Record<string, string> = {
    '440x956': 'iPhone 16 Pro Max / 17 Pro Max',
    '402x874': 'iPhone 16 Pro / 17 Pro',
    '430x932': 'iPhone 14 Pro Max / 15 Pro Max / 16 Plus',
    '393x852': 'iPhone 14 Pro / 15 Pro / 16',
    '390x844': 'iPhone 12 / 13 / 14 / 15 / 16e',
    '428x926': 'iPhone 12 Pro Max / 13 Pro Max / 14 Plus',
    '414x896': 'iPhone XS Max / 11 Pro Max',
    '375x812': 'iPhone X / 11 Pro / 12 mini / 13 mini',
  };

  return {
    phoneModel: fallbackMap[screenKey] || `iPhone (${screenKey})`,
    brand: 'Apple',
    confidence: 'approximate',
  };
}

function parseAndroidModel(userAgent: string) {
  const build = userAgent.match(/Android[^;]*;\s*([^;)]+)\s*Build/i);
  if (build && build[1]) {
    const model = build[1].trim();
    if (model && !/linux|android|mobile/i.test(model)) {
      return { phoneModel: model, brand: null, confidence: 'exact' as const };
    }
  }

  const samsung = userAgent.match(/SM-[A-Z0-9]+/i);
  if (samsung) {
    return { phoneModel: samsung[0], brand: 'Samsung', confidence: 'exact' as const };
  }

  return {
    phoneModel: 'Android device',
    brand: null,
    confidence: 'approximate' as const,
  };
}

function parseBrowser(userAgent: string) {
  if (/FxiOS\/([\d.]+)/i.test(userAgent)) return `Firefox iOS ${RegExp.$1}`;
  if (/CriOS\/([\d.]+)/i.test(userAgent)) return `Chrome iOS ${RegExp.$1}`;
  if (/Version\/([\d.]+).*Safari/i.test(userAgent)) return `Mobile Safari ${RegExp.$1}`;
  if (/Chrome\/([\d.]+)/i.test(userAgent)) return `Chrome ${RegExp.$1}`;
  if (/Firefox\/([\d.]+)/i.test(userAgent)) return `Firefox ${RegExp.$1}`;
  return navigator.userAgent || null;
}

export async function detectDevice(slug: string): Promise<DevicePayload> {
  const userAgent = navigator.userAgent;
  const { width, height, key } = getScreenKey();
  const pixelRatio = window.devicePixelRatio || 1;
  const browser = parseBrowser(userAgent);
  const gpuRenderer = getWebGlRenderer();

  if (/iPhone|iPad/i.test(userAgent)) {
    const iosVersion = parseIosVersion(userAgent);
    const apple = parseAppleModel(userAgent, key, gpuRenderer);

    return {
      slug,
      phoneModel: apple.phoneModel,
      brand: apple.brand,
      deviceType: /iPad/i.test(userAgent) ? 'tablet' : 'mobile',
      os: 'iOS',
      osVersion: iosVersion,
      androidVersion: null,
      iosVersion,
      browser,
      userAgent,
      screenWidth: width,
      screenHeight: height,
      pixelRatio,
      screenKey: key,
      gpuRenderer,
      modelSource: 'client-detect',
      modelConfidence: apple.confidence,
    };
  }

  if (/Android/i.test(userAgent)) {
    const androidVersion = userAgent.match(/Android\s+([\d.]+)/i)?.[1] || null;
    const android = parseAndroidModel(userAgent);

    return {
      slug,
      phoneModel: android.phoneModel,
      brand: android.brand,
      deviceType: 'mobile',
      os: 'Android',
      osVersion: androidVersion,
      androidVersion,
      iosVersion: null,
      browser,
      userAgent,
      screenWidth: width,
      screenHeight: height,
      pixelRatio,
      screenKey: key,
      gpuRenderer,
      modelSource: 'client-detect',
      modelConfidence: android.confidence,
    };
  }

  return {
    slug,
    phoneModel: 'Desktop / Unknown',
    brand: null,
    deviceType: 'desktop',
    os: null,
    osVersion: null,
    androidVersion: null,
    iosVersion: null,
    browser,
    userAgent,
    screenWidth: width,
    screenHeight: height,
    pixelRatio,
    screenKey: key,
    gpuRenderer,
    modelSource: 'client-detect',
    modelConfidence: 'approximate',
  };
}
